import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Download, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ categoryName: string; error: string }>;
  created: number;
  updated: number;
}

interface CategoryImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ImportResult>;
  onDownloadTemplate: () => void;
  onExportCurrent: () => void;
}

const CategoryImportModal: React.FC<CategoryImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onDownloadTemplate,
  onExportCurrent,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith(".csv")) {
        setFile(droppedFile);
        setImportResult(null);
        setParseErrors([]);
      } else {
        alert("Please upload a CSV file");
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setImportResult(null);
      setParseErrors([]);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsProcessing(true);
    setImportResult(null);
    setParseErrors([]);

    try {
      // Send file directly to backend
      const result = await onImport(file);
      
      // Handle result
      if (result.failed > 0 && result.success === 0) {
        // All failed - show as parse errors
        setParseErrors(result.errors.map(e => `${e.categoryName}: ${e.error}`));
      } else {
        // Some or all succeeded
        setImportResult(result);
      }
    } catch (error: any) {
      setParseErrors([`Error processing file: ${error.message}`]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setParseErrors([]);
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Import Categories from CSV</h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full"
                disabled={isProcessing}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Instructions */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h3 className="font-medium text-blue-900 mb-2">Instructions</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Download the template CSV file to see the required format</li>
                <li>Fill in your category data following the template structure</li>
                <li>Use pipe (|) to separate multiple values (requirements, subcategories)</li>
                <li>Type: 1=Home Services, 2=Professional Services, 3=International Services</li>
                <li>If a category name already exists, it will be updated; otherwise, it will be created</li>
                <li>Add translations for each language in the corresponding columns</li>
              </ul>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={onDownloadTemplate}
                variant="outline"
                className="flex items-center gap-2 flex-1"
                disabled={isProcessing}
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              <Button
                onClick={onExportCurrent}
                variant="outline"
                className="flex items-center gap-2 flex-1"
                disabled={isProcessing}
              >
                <Download className="h-4 w-4" />
                Export Current Categories
              </Button>
            </div>

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {file ? file.name : "Drop your CSV file here"}
              </p>
              <p className="text-sm text-gray-500 mb-4">or click to browse</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
                disabled={isProcessing}
              />
              <label htmlFor="csv-upload">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("csv-upload")?.click()}
                  disabled={isProcessing}
                >
                  Select File
                </Button>
              </label>
            </div>

            {/* Parse Errors */}
            {parseErrors.length > 0 && (
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-red-900 mb-2">Parsing Errors</h3>
                    <div className="text-sm text-red-800 space-y-1 max-h-40 overflow-y-auto">
                      {parseErrors.map((error, index) => (
                        <div key={index}>• {error}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Import Result */}
            {importResult && (
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-green-900 mb-2">Import Complete</h3>
                    <div className="text-sm text-green-800 space-y-1">
                      <div>✓ Successfully processed: {importResult.success} categories</div>
                      <div>✓ Created: {importResult.created} categories</div>
                      <div>✓ Updated: {importResult.updated} categories</div>
                      {importResult.failed > 0 && (
                        <div className="text-orange-700 mt-2">
                          ⚠ Failed: {importResult.failed} categories
                        </div>
                      )}
                      {importResult.errors.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto">
                          <div className="font-medium text-orange-800 mb-1">Errors:</div>
                          {importResult.errors.map((error, index) => (
                            <div key={index} className="text-orange-700">
                              • {error.categoryName}: {error.error}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Import Button */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isProcessing}
              >
                {importResult ? "Close" : "Cancel"}
              </Button>
              <Button
                onClick={handleImport}
                disabled={!file || isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Import Categories
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CategoryImportModal;

